import { AppliedCouponResponseDTO } from '@/DTO/checkoutResponseDTO';
import { CartValuesServiceStore } from '@/store/shoppingCart/shoppingCartStore';

/**
 * Arquivo para regras de negócio e validações relacionadas a cupons de desconto.
 * 
 * TODO: Implementar validações específicas conforme regras do backend:
 * - Validar valor mínimo do carrinho
 * - Validar tipos de produtos elegíveis (waves, tickets, park, etc.)
 * - Validar limite de produtos por uso
 * - Validar perfil do usuário (usedFor)
 * - Validar wave level (applyToWaveLevel)
 * - Outras regras específicas do cupom
 */

export interface CouponValidationResult {
    isValid: boolean;
    errorMessage?: string;
    warningMessage?: string;
}

/**
 * Valida se o cupom pode ser aplicado ao carrinho atual
 * @param coupon - Cupom aplicado
 * @param cartItems - Itens do carrinho
 * @param cartTotal - Valor total do carrinho
 * @returns Resultado da validação
 */
export const validateCouponForCart = (
    coupon: AppliedCouponResponseDTO | null
): CouponValidationResult => {
    // Validação mínima: checa existência do cupom
    if (!coupon) {
        return { isValid: false, errorMessage: 'Nenhum cupom aplicado' };
    }

    // Atualmente validamos apenas constraint de waveLevel (applyToWaveLevel)
    if (coupon.applyToWaveLevel) {
        // A validação mais detalhada depende dos itens do carrinho
        // Essa função pode ser usada com um array de itens quando necessário
        return { isValid: true };
    }

    return { isValid: true };
};

/**
 * Mapear applyToWaveLevel (número) para string correspondente
 */
export const mapApplyToWaveLevel = (applyToWaveLevel?: number): string | null => {
    if (applyToWaveLevel === 1) return 'Bay';
    if (applyToWaveLevel === 2) return 'Reef';
    return null;
};

/**
 * Valida se um cupom é compatível com um array de itens do carrinho.
 * Retorna objeto com isValid e mensagens (error/warning) apropriadas.
 */
export const validateCouponAgainstCartItems = (
    coupon: AppliedCouponResponseDTO | null,
    cartItems: CartValuesServiceStore[] | null | undefined
): CouponValidationResult => {
    if (!coupon) return { isValid: false, errorMessage: 'Nenhum cupom aplicado' };

    if (coupon.applyToWaveLevel && cartItems) {
        const target = mapApplyToWaveLevel(coupon.applyToWaveLevel);
        if (!target) return { isValid: true };

        // Se o cupom aponta um wave level específico (Bay/Reef),
        // então itens sem waveLevel ou com waveLevel diferente são inválidos.
        const hasItemWithoutWave = cartItems.some((i) => typeof i.waveLevel === 'undefined');
        if (hasItemWithoutWave) {
            return { isValid: false, errorMessage: `Este cupom só pode ser aplicado em produtos do tipo ${target}.` };
        }

        const hasAnyCompatible = cartItems.some((i) => i.waveLevel === target);
        const hasAnyIncompatible = cartItems.some((i) => i.waveLevel !== target);

        if (!hasAnyCompatible) {
            return { isValid: false, errorMessage: `Este cupom só pode ser aplicado em produtos do tipo ${target}.` };
        }

        if (hasAnyIncompatible) {
            return { isValid: false, errorMessage: `Este cupom só pode ser aplicado em carrinhos com todos os produtos do tipo ${target}.` };
        }
    }

    return { isValid: true };
};

/**
 * Retorna true se o cupom deve ser aplicado ao item específico
 */
export const isCouponApplicableToItem = (
    coupon: AppliedCouponResponseDTO | null,
    item: CartValuesServiceStore | null | undefined
): boolean => {
    if (!coupon) return false;
    if (!coupon.applyToWaveLevel) return true;
    const target = mapApplyToWaveLevel(coupon.applyToWaveLevel);
    if (!target) return true;
    if (!item) return false;
    return item.waveLevel !== undefined && item.waveLevel === target;
};

/**
 * Calcula o valor final após aplicação do cupom
 * @param originalValue - Valor original do carrinho
 * @param coupon - Cupom aplicado
 * @returns Valor final com desconto aplicado
 */
export const calculateDiscountedValue = (
    originalValue: number,
    coupon: AppliedCouponResponseDTO | null
): number => {
    if (!coupon || !coupon.discountValue) {
        return originalValue;
    }

    const discountedValue = originalValue - coupon.discountValue;
    return Math.max(0, discountedValue); // Não permitir valores negativos
};

/**
 * Verifica se um produto específico é elegível para o cupom
 * @param productType - Tipo do produto (waves, tickets, park, etc.)
 * @param coupon - Cupom aplicado
 * @returns true se o produto é elegível
 */
export const isProductEligibleForCoupon = (
    coupon: AppliedCouponResponseDTO | null
): boolean => {
    // TODO: Implementar lógica baseada em usedFor e outras regras do cupom
    if (!coupon) return false;
    
    // Exemplo de implementação futura:
    // if (coupon.usedFor === 1 && productType !== 'store') {
    //     return false;
    // }
    
    return true;
};

/**
 * Formata mensagem de erro do backend para exibição amigável
 * @param backendParameter - Parâmetro retornado pelo backend
 * @returns Mensagem amigável
 */
export const formatCouponErrorMessage = (backendParameter: string): string => {
    const errorMessages: Record<string, string> = {
        'DiscountCoupon.UnauthorizedUse': 'Cupom não pode ser usado neste contexto',
        'DiscountCoupon.Unavailable': 'Cupom indisponível para uso',
        'DiscountCoupon.Expired': 'Cupom expirado',
        'DiscountCoupon.LimitReached': 'Limite de uso do cupom atingido',
        'DiscountCoupon.MinimumValueNotReached': 'Valor mínimo para uso do cupom não atingido',
        'DiscountCoupon.ProductNotEligible': 'Um ou mais produtos não são elegíveis para este cupom',
    };

    return errorMessages[backendParameter] || 'Erro ao aplicar cupom';
};
